import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Plus, Trash2, Check, Square, ClipboardList, Ruler,
  Calendar, MapPin, User, IndianRupee, FileText, Download
} from "lucide-react";
import { toast } from "sonner";
import { MEASUREMENT_TEMPLATES } from "@/lib/measurementTemplates";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [woOpen, setWoOpen] = useState(false);
  const [woForm, setWoForm] = useState(emptyWO());
  const [woEditing, setWoEditing] = useState(null);

  const [mOpen, setMOpen] = useState(false);
  const [mForm, setMForm] = useState({ template: "Door", fields: {}, quantity: 1, rate: 0, unit: "Sq.ft" });

  function emptyWO() {
    return { title: "", site_address: "", assigned_to: "", status: "Pending", checklist: [], materials: "" };
  }

  const load = async () => {
    setLoading(true);
    try {
      const [p, w, m] = await Promise.all([
        api.get(`/projects/${id}/detail`),
        api.get(`/projects/${id}/work-orders`),
        api.get(`/projects/${id}/measurements`),
      ]);
      setProject(p.data);
      setWorkOrders(w.data);
      setMeasurements(m.data);
    } catch {
      toast.error("Failed to load project");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  // ---- Work Orders ----
  const openCreateWO = () => { setWoEditing(null); setWoForm(emptyWO()); setWoOpen(true); };
  const openEditWO = (wo) => { setWoEditing(wo); setWoForm({ ...wo, checklist: wo.checklist || [] }); setWoOpen(true); };
  const saveWO = async () => {
    if (!woForm.title) return toast.error("Title required");
    try {
      if (woEditing) {
        await api.put(`/work-orders/${woEditing.id}`, woForm);
        toast.success("Work order updated");
      } else {
        await api.post(`/projects/${id}/work-orders`, woForm);
        toast.success("Work order created");
      }
      setWoOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const removeWO = async (woId) => {
    if (!window.confirm("Delete work order?")) return;
    await api.delete(`/work-orders/${woId}`); load();
  };
  const toggleChecklistItem = (idx) => {
    const cl = [...(woForm.checklist || [])];
    cl[idx] = { ...cl[idx], done: !cl[idx].done };
    setWoForm({ ...woForm, checklist: cl });
  };
  const addChecklistItem = () => setWoForm({ ...woForm, checklist: [...(woForm.checklist || []), { text: "", done: false }] });
  const updateChecklistText = (idx, text) => {
    const cl = [...(woForm.checklist || [])];
    cl[idx] = { ...cl[idx], text };
    setWoForm({ ...woForm, checklist: cl });
  };
  const removeChecklistItem = (idx) => setWoForm({ ...woForm, checklist: woForm.checklist.filter((_, i) => i !== idx) });

  // ---- Measurements ----
  const openCreateM = () => {
    const t = MEASUREMENT_TEMPLATES.Door;
    setMForm({ template: "Door", fields: {}, quantity: 1, rate: 0, unit: t.unit, location: "" });
    setMOpen(true);
  };
  const onTemplateChange = (template) => {
    const t = MEASUREMENT_TEMPLATES[template];
    setMForm({ ...mForm, template, fields: {}, unit: t.unit });
  };
  const updateMField = (key, val) => setMForm({ ...mForm, fields: { ...mForm.fields, [key]: val } });
  const saveM = async () => {
    try {
      await api.post(`/projects/${id}/measurements`, mForm);
      toast.success("Measurement added");
      setMOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const removeM = async (mid) => {
    if (!window.confirm("Delete measurement?")) return;
    await api.delete(`/measurements/${mid}`); load();
  };

  const totalMeasurement = measurements.reduce((s, m) => s + (m.amount || 0), 0);
  const completedWO = workOrders.filter((w) => w.status === "Completed").length;
  const woProgress = workOrders.length ? Math.round((completedWO / workOrders.length) * 100) : 0;

  if (loading) return <div className="text-muted-foreground text-sm">Loading project…</div>;
  if (!project) return (
    <div className="text-center py-20">
      <div className="text-muted-foreground">Project not found.</div>
      <Button variant="outline" className="mt-4" onClick={() => nav("/projects")}><ArrowLeft size={14} className="mr-1" /> Back</Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav("/projects")} data-testid="project-back">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{project.name}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
              {project.customer_name && <span className="flex items-center gap-1"><User size={12} /> {project.customer_name}</span>}
              {project.site_address && <span className="flex items-center gap-1"><MapPin size={12} /> {project.site_address}</span>}
              {project.start_date && <span className="flex items-center gap-1"><Calendar size={12} /> {project.start_date} → {project.end_date || "—"}</span>}
            </div>
          </div>
        </div>
        <Badge variant={project.status === "Completed" ? "default" : "secondary"} className="text-xs">{project.status}</Badge>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Budget</div>
          <div className="font-heading font-bold text-2xl mt-1">{inr(project.budget || 0)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Material + Labour</div>
          <div className="font-heading font-bold text-2xl mt-1">{inr((project.material_cost || 0) + (project.labour_cost || 0))}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Work Orders</div>
          <div className="font-heading font-bold text-2xl mt-1">{workOrders.length}</div>
          {workOrders.length > 0 && (
            <Progress value={woProgress} className="mt-2 h-1.5" />
          )}
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Measurements Total</div>
          <div className="font-heading font-bold text-2xl mt-1">{inr(totalMeasurement)}</div>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="work-orders" data-testid="tab-work-orders">Work Orders ({workOrders.length})</TabsTrigger>
          <TabsTrigger value="measurements" data-testid="tab-measurements">Measurements ({measurements.length})</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="font-heading font-bold text-lg mb-4">Project Details</div>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Status" value={project.status} />
                <Field label="Progress" value={`${project.progress || 0}%`} />
                <Field label="Assigned To" value={project.assigned_to} />
                <Field label="Customer" value={project.customer_name} />
                <Field label="Start" value={project.start_date} />
                <Field label="End" value={project.end_date} />
                <Field label="Material Cost" value={inr(project.material_cost || 0)} />
                <Field label="Labour Cost" value={inr(project.labour_cost || 0)} />
              </dl>
              {project.notes && (
                <div className="mt-4 p-3 bg-muted/40 rounded-lg">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
                  <div className="text-sm whitespace-pre-wrap">{project.notes}</div>
                </div>
              )}
            </Card>
            <Card className="p-6">
              <div className="font-heading font-bold text-lg mb-4">Recent Activity</div>
              {workOrders.length === 0 && measurements.length === 0 ? (
                <div className="text-sm text-muted-foreground">No work orders or measurements yet. Add them from the tabs above.</div>
              ) : (
                <div className="space-y-2 text-sm">
                  {workOrders.slice(0, 5).map((w) => (
                    <div key={w.id} className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-0">
                      <ClipboardList size={14} className="text-primary" />
                      <span className="flex-1 truncate">{w.title}</span>
                      <Badge variant="outline" className="text-xs">{w.status}</Badge>
                    </div>
                  ))}
                  {measurements.slice(0, 5).map((m) => (
                    <div key={m.id} className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-0">
                      <Ruler size={14} className="text-primary" />
                      <span className="flex-1 truncate">{m.template} {m.location ? `· ${m.location}` : ""}</span>
                      <span className="text-xs text-muted-foreground">{inr(m.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Work Orders */}
        <TabsContent value="work-orders" className="mt-4">
          <div className="flex justify-between items-end mb-4">
            <div className="text-sm text-muted-foreground">{completedWO}/{workOrders.length} completed</div>
            <Button onClick={openCreateWO} data-testid="add-work-order"><Plus size={14} className="mr-1" /> New Work Order</Button>
          </div>
          {workOrders.length === 0 ? (
            <Card className="p-16 text-center">
              <ClipboardList size={36} className="mx-auto text-muted-foreground" />
              <div className="mt-4 text-sm text-muted-foreground">No work orders yet. Click &apos;New Work Order&apos; to assign tasks.</div>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {workOrders.map((w) => {
                const cl = w.checklist || [];
                const done = cl.filter((c) => c.done).length;
                return (
                  <Card key={w.id} className="p-5" data-testid={`wo-card-${w.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">{w.wo_number}</div>
                        <div className="font-heading font-bold text-base truncate">{w.title}</div>
                      </div>
                      <Badge variant={w.status === "Completed" ? "default" : w.status === "In Progress" ? "secondary" : "outline"}>{w.status}</Badge>
                    </div>
                    {w.site_address && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={11} /> {w.site_address}</div>}
                    {w.assigned_to && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><User size={11} /> {w.assigned_to}</div>}
                    {cl.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs text-muted-foreground mb-1.5">Checklist · {done}/{cl.length}</div>
                        <Progress value={(done / cl.length) * 100} className="h-1.5" />
                        <ul className="mt-2 space-y-0.5">
                          {cl.slice(0, 4).map((c, i) => (
                            <li key={i} className={`text-xs flex items-center gap-1.5 ${c.done ? "line-through text-muted-foreground" : ""}`}>
                              {c.done ? <Check size={11} className="text-emerald-600" /> : <Square size={11} />} {c.text}
                            </li>
                          ))}
                          {cl.length > 4 && <li className="text-xs text-muted-foreground">+ {cl.length - 4} more…</li>}
                        </ul>
                      </div>
                    )}
                    <div className="flex justify-end gap-1 mt-3">
                      <Button variant="ghost" size="sm" onClick={() => openEditWO(w)} data-testid={`wo-edit-${w.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => removeWO(w.id)} className="text-destructive" data-testid={`wo-delete-${w.id}`}>Delete</Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Measurements */}
        <TabsContent value="measurements" className="mt-4">
          <div className="flex justify-between items-end mb-4">
            <div className="text-sm text-muted-foreground">Total amount: <span className="font-semibold text-foreground">{inr(totalMeasurement)}</span></div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.print()} data-testid="measurements-export"><Download size={14} className="mr-1" /> Export PDF</Button>
              <Button onClick={openCreateM} data-testid="add-measurement"><Plus size={14} className="mr-1" /> Add Measurement</Button>
            </div>
          </div>

          {measurements.length === 0 ? (
            <Card className="p-16 text-center">
              <Ruler size={36} className="mx-auto text-muted-foreground" />
              <div className="mt-4 text-sm text-muted-foreground">No measurements yet. Pick a template (Door, Wardrobe, Kitchen…) to start.</div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Specs</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {measurements.map((m) => {
                    const t = MEASUREMENT_TEMPLATES[m.template] || MEASUREMENT_TEMPLATES.Custom;
                    const summary = Object.entries(m.fields || {})
                      .filter(([k, v]) => v !== "" && v != null)
                      .slice(0, 3)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ");
                    return (
                      <TableRow key={m.id}>
                        <TableCell><span className="mr-1">{t.icon}</span><span className="font-medium">{m.template}</span></TableCell>
                        <TableCell>{m.location || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-md truncate">{summary}</TableCell>
                        <TableCell>{m.quantity} {m.unit}</TableCell>
                        <TableCell>{inr(m.rate)}</TableCell>
                        <TableCell className="font-semibold">{inr(m.amount || 0)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => removeM(m.id)} className="text-destructive"><Trash2 size={14} /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Work Order Dialog */}
      <Dialog open={woOpen} onOpenChange={setWoOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{woEditing ? "Edit" : "New"} Work Order</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Title *</Label>
                <Input value={woForm.title} onChange={(e) => setWoForm({ ...woForm, title: e.target.value })} data-testid="wo-title" />
              </div>
              <div>
                <Label>Site Address</Label>
                <Input value={woForm.site_address} onChange={(e) => setWoForm({ ...woForm, site_address: e.target.value })} />
              </div>
              <div>
                <Label>Assigned To</Label>
                <Input value={woForm.assigned_to} onChange={(e) => setWoForm({ ...woForm, assigned_to: e.target.value })} />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={woForm.start_date || ""} onChange={(e) => setWoForm({ ...woForm, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={woForm.end_date || ""} onChange={(e) => setWoForm({ ...woForm, end_date: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={woForm.status} onChange={(e) => setWoForm({ ...woForm, status: e.target.value })} data-testid="wo-status">
                  {["Pending", "In Progress", "Completed", "Cancelled"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Materials</Label>
                <Input value={woForm.materials} onChange={(e) => setWoForm({ ...woForm, materials: e.target.value })} placeholder="Plywood, hinges, paint…" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Checklist</Label>
                <Button size="sm" variant="outline" onClick={addChecklistItem}><Plus size={12} /> Item</Button>
              </div>
              <div className="space-y-2">
                {(woForm.checklist || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <button onClick={() => toggleChecklistItem(i)} className="text-muted-foreground hover:text-primary">
                      {c.done ? <Check size={18} className="text-emerald-600" /> : <Square size={18} />}
                    </button>
                    <Input value={c.text} onChange={(e) => updateChecklistText(i, e.target.value)} placeholder="Task description" className="flex-1" />
                    <Button variant="ghost" size="icon" onClick={() => removeChecklistItem(i)}><Trash2 size={14} className="text-destructive" /></Button>
                  </div>
                ))}
                {(woForm.checklist || []).length === 0 && (
                  <div className="text-xs text-muted-foreground p-2">No checklist items. Click &apos;Item&apos; to add one.</div>
                )}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-background" rows={2} value={woForm.notes || ""} onChange={(e) => setWoForm({ ...woForm, notes: e.target.value })} />
            </div>
            <div>
              <Label>Completion Report</Label>
              <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-background" rows={2} value={woForm.completion_report || ""} onChange={(e) => setWoForm({ ...woForm, completion_report: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWoOpen(false)}>Cancel</Button>
            <Button onClick={saveWO} data-testid="wo-save-btn">{woEditing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Measurement Dialog */}
      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Measurement</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Template</Label>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mt-2">
                {Object.entries(MEASUREMENT_TEMPLATES).map(([key, t]) => (
                  <button
                    key={key}
                    onClick={() => onTemplateChange(key)}
                    data-testid={`template-${key}`}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${mForm.template === key ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-muted"}`}
                  >
                    <span className="text-xl">{t.icon}</span>
                    <span className="text-[10px] font-medium leading-tight text-center">{key}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Location / Room</Label>
                <Input value={mForm.location || ""} onChange={(e) => setMForm({ ...mForm, location: e.target.value })} placeholder="e.g. Master Bedroom" data-testid="m-location" />
              </div>
              {(MEASUREMENT_TEMPLATES[mForm.template]?.fields || []).map((f) => (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}</Label>
                  {f.type === "select" ? (
                    <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={mForm.fields[f.key] || ""} onChange={(e) => updateMField(f.key, e.target.value)} data-testid={`mfield-${f.key}`}>
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input
                      type={f.type || "text"}
                      value={mForm.fields[f.key] || ""}
                      onChange={(e) => updateMField(f.key, f.type === "number" ? (e.target.value === "" ? "" : parseFloat(e.target.value)) : e.target.value)}
                      data-testid={`mfield-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input type="number" value={mForm.quantity} onChange={(e) => setMForm({ ...mForm, quantity: parseFloat(e.target.value) || 0 })} data-testid="m-quantity" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={mForm.unit} onChange={(e) => setMForm({ ...mForm, unit: e.target.value })} />
              </div>
              <div>
                <Label>Rate (₹ per unit)</Label>
                <Input type="number" value={mForm.rate} onChange={(e) => setMForm({ ...mForm, rate: parseFloat(e.target.value) || 0 })} data-testid="m-rate" />
              </div>
            </div>

            <Card className="p-3 bg-muted/30 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Amount</span>
              <span className="font-heading font-bold text-lg" data-testid="m-amount">{inr((mForm.quantity || 0) * (mForm.rate || 0))}</span>
            </Card>

            <div>
              <Label>Notes</Label>
              <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-background" rows={2} value={mForm.notes || ""} onChange={(e) => setMForm({ ...mForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMOpen(false)}>Cancel</Button>
            <Button onClick={saveM} data-testid="m-save-btn">Add Measurement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd className="font-medium mt-0.5">{value || "—"}</dd>
    </div>
  );
}
